import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import { Boton } from '#components/mobile/ui/Boton';
import { CampoImporte } from '#components/mobile/ui/CampoImporte';
import { color } from '#components/mobile/ui/tokens';

import {
  estiloCampo,
  estiloOpcion,
  estiloOpciones,
  Etiqueta,
  HojaTravesia,
  Marca,
  TituloHoja,
} from './comun';

/** Lo que se elige: un tipo de nómina o «otra cosa» (no se guarda nada). */
export type EleccionNomina = '12' | '14' | 'prorrateadas' | 'otra';

type HojaNominaProps = {
  ocupado?: boolean;
  onContinuar: (eleccion: EleccionNomina | null, neta: number | null) => void;
};

/**
 * Paso 3, segunda parte · «¿Cobras una nómina?» (maqueta 6 de las pagas
 * extra): tras dar de alta la cuenta, Gavi pregunta cuántas pagas cobra la
 * persona y, si quiere, cuánto. Con eso Inicio cuenta los días hasta cada
 * paga extra. Se puede seguir sin elegir nada.
 */
export function HojaNomina({ ocupado, onContinuar }: HojaNominaProps) {
  const { t } = useTranslation();
  const [eleccion, setEleccion] = useState<EleccionNomina | null>(null);
  const [neta, setNeta] = useState<number | null>(null);

  const opciones: {
    valor: EleccionNomina;
    titulo: string;
    detalle?: string;
  }[] = [
    {
      valor: '12',
      titulo: t('{{count}} payments', { count: 12 }),
      detalle: t('The same every month'),
    },
    {
      valor: '14',
      titulo: t('{{count}} payments', { count: 14 }),
      detalle: t('Extra in summer and at Christmas'),
    },
    {
      valor: 'prorrateadas',
      titulo: t('Prorated pay'),
      detalle: t('The extras already come inside each month'),
    },
    { valor: 'otra', titulo: t('Something else, or no payslip') },
  ];

  const conImporte = eleccion != null && eleccion !== 'otra';

  return (
    <HojaTravesia
      aria-label={t('Do you get a payslip?')}
      data-testid="hoja-nomina"
      cabeza={
        <Bocadillo
          variante="hoja"
          estado={eleccion === '14' ? 'aplaude' : 'reposo'}
          texto={
            eleccion === '14'
              ? t('Two high tides a year! I will warn you before each one.')
              : t(
                  'If you tell me how many payments you get, I will warn you before each extra one so it does not vanish.',
                )
          }
        />
      }
      pie={
        <Boton
          bloque
          isDisabled={ocupado}
          data-testid="travesia-continuar"
          onPress={() => onContinuar(eleccion, conImporte ? neta : null)}
        >
          <Trans>Continue</Trans>
        </Boton>
      }
    >
      <TituloHoja>
        <Trans>Do you get a payslip?</Trans>
      </TituloHoja>
      <div style={estiloOpciones}>
        {opciones.map((o, i) => (
          <button
            type="button"
            key={o.valor}
            aria-pressed={eleccion === o.valor}
            data-testid={`nomina-${o.valor}`}
            style={estiloOpcion(i === 0)}
            onClick={() => setEleccion(o.valor)}
          >
            <span style={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
              {o.titulo}
              {o.detalle && (
                <span
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 500,
                    color: color.fg3,
                  }}
                >
                  {o.detalle}
                </span>
              )}
            </span>
            <Marca marcada={eleccion === o.valor} />
          </button>
        ))}
      </div>
      {conImporte && (
        <div style={{ marginTop: 12 }}>
          <Etiqueta htmlFor="travesia-nomina-neta">
            <Trans>How much reaches you each month, more or less?</Trans>
          </Etiqueta>
          <CampoImporte
            id="travesia-nomina-neta"
            aria-label={t('How much reaches you each month, more or less?')}
            data-testid="travesia-nomina-neta"
            valor={neta}
            opcional
            onCambiar={setNeta}
            style={{ ...estiloCampo, justifyContent: 'flex-start' }}
          />
        </div>
      )}
    </HojaTravesia>
  );
}
